import React, { useCallback } from "react";
import * as Scrivito from "scrivito";
import NiceModal from "@ebay/nice-modal-react";
import {
  ModalWrapper,
  Button,
  ProjectDetailsModal,
  ProductItem,
} from "components";
import { Label, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import cx from "classnames";

import styles from "./ProjectAddingModal.module.scss";
import { useFetchProjectList } from "./hooks/useFetchProjectList";
import {
  useCopiedItem,
  useHasAddedNode,
  useResetAddedProductStore,
  useResetClipboard,
} from "stores/project";
import { useTranslate } from "hooks";
import { useRestoreProject } from "hooks/projectHooks";
import { isAnyProduct } from "utils/treeUtils";
import { ProjectSelect } from "./ProjectSelect";
import { useIsSameProj } from "stores/globalStore";
import { ProjectCreateMut, SameProjectCreateMut } from "api/mutations";
import { useChangeProject } from "./hooks/useChangeProject";
import { ProjectStructure } from "./ProjectStructure";

export const ProductAddingModal = NiceModal.create(
  Scrivito.connect(() => {
    const modal = NiceModal.useModal();
    const translate = useTranslate();
    const copiedItem = useCopiedItem();
    const resetClipboard = useResetClipboard();

    const hasAddedNode = useHasAddedNode();
    const resetAddedProducts = useResetAddedProductStore();

    const isSameProj = useIsSameProj();
    const changeProject = useChangeProject();

    // Cleanup hook: Once modal closes, if the project was changed, restore the original project.
    useRestoreProject();

    const [{ data, fetching, stale }, exeGetProjectsOptions] =
      useFetchProjectList();

    const handleCloseModal = useCallback(() => {
      resetClipboard();
      resetAddedProducts();
      modal.remove();
    }, [modal, resetAddedProducts, resetClipboard]);

    const openCreateProjectModal = useCallback(() => {
      NiceModal.show(ProjectDetailsModal, {
        mutation: isSameProj ? SameProjectCreateMut : ProjectCreateMut,
        requestParams: copiedItem?.data,
      }).then((response) => {
        // Todo, once ProjectDetailsModal is refactored to typescript, strongly type this response
        const { id, title } = response as { id: string; title: string };
        exeGetProjectsOptions();
        changeProject({ value: id, label: title });
      });
    }, [isSameProj, copiedItem?.data, exeGetProjectsOptions, changeProject]);

    if (!copiedItem || !isAnyProduct(copiedItem?.data)) {
      return <>An error occurred</>;
    }

    return (
      <ModalWrapper
        isOpen={modal.visible}
        centered={true}
        size="xl"
        className={styles.modalWrapper}
        toggle={modal.hide}
        onClosed={handleCloseModal}
        isLoading={fetching || stale}
      >
        <ModalHeader
          toggle={modal.hide}
          tag="div"
          className="pb-0 d-flex align-items-start"
        >
          <h3 className="text-uppercase mb-1">{translate("ADD_TO_PROJECT")}</h3>
          <h6 className="mb-0 text-break">{copiedItem?.data?.productName}</h6>
        </ModalHeader>
        <ModalBody className={cx("pt-3 row d-flex", styles.bodyHeight)}>
          <div className="col-lg-6 col-12 d-flex">
            <div className="border rounded-1 mr-2 px-3 py-4 w-100">
              <h5 className="mb-1">
                {translate("ADD_PRODUCT_MODAL_PRODUCT_TITLE")}
              </h5>
              <span className="text-gray-500">
                {translate("ADD_PRODUCT_MODAL_PRODUCT_SUBTITLE")}
              </span>
              <div className="mt-5 h-100">
                <ProductItem />
              </div>
            </div>
          </div>
          <div className="col-lg-6 col-12 d-flex flex-column h-100">
            <div className="border rounded-1 mr-2 d-flex flex-column px-3 pt-4 w-100 h-100">
              <div className="mb-5_5 d-flex flex-shrink-0 align-items-end justify-content-between">
                <div>
                  <Label
                    className={cx(
                      "d-flex align-items-center justify-content-between mb-1 fs-16 fw-bolder text-gray-700 noTextSelection",
                      styles.noselect,
                    )}
                    for="productSelect"
                  >
                    {translate("ADD_PRODUCT_MODAL_SELECT_LABEL")}
                  </Label>
                  <div className="d-flex justify-content-between align-items-center">
                    <div
                      className="d-flex flex-column flex-grow-1"
                      style={{ width: "300px" }}
                    >
                      <ProjectSelect data={data} />
                    </div>
                  </div>
                </div>
                <span className="pb-2">
                  {translate("ADD_PRODUCT_MODAL_OR")}
                </span>
                <Button
                  data-testid="ProductAddingModal_create-new-project-btn"
                  color="secondary"
                  onClick={openCreateProjectModal}
                >
                  {translate("ADD_PRODUCT_MODAL_CREATE_NEW_PROJECT")}
                </Button>
              </div>
              <ProjectStructure />
            </div>
          </div>
        </ModalBody>
        <ModalFooter className="pt-0">
          <Button
            data-testid="ProductAddingModal_done-btn"
            onClick={handleCloseModal}
            disabled={!hasAddedNode}
          >
            {translate("PRODUCT_CONFIGURATOR_DONE")}
          </Button>
        </ModalFooter>
      </ModalWrapper>
    );
  }),
);
